import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import AccountStatement from './AccountStatement';
import { SectionHeading } from './SectionHeading';
import { TransactionSection } from './TransactionSection/TransactionSection';
import { ApplicationSection } from './ApplicationSection/ApplicationSection';
import { useMe, useSavingsFundBalance, useSourceFunds } from '../common/apiHooks';
import { PII_CLASS } from '../tracking/piiMarkup';
import { SavingsFundStatementSection } from './Portfolio/SavingsFundStatementSection';

export function RepresentedPartyAccountPage() {
  const { data: user } = useMe();
  const { data: savingsFundBalance, isLoading: savingsFundBalanceLoading } =
    useSavingsFundBalance();

  // Only a represented person (e.g. a parent viewing a child) can have a third pillar;
  // a represented legal entity (company) never does, so skip the request for them.
  const isRepresentedPerson = user?.role?.type === 'PERSON';
  const { data: sourceFunds } = useSourceFunds(undefined, undefined, {
    enabled: isRepresentedPerson,
  });
  const thirdPillarFunds = (sourceFunds ?? []).filter((fund) => fund.pillar === 3);
  const showThirdPillar = isRepresentedPerson && thirdPillarFunds.length > 0;
  // The statement is for a company's accountant; a child's account doesn't need one.
  const isRepresentedCompany = user?.role?.type === 'LEGAL_ENTITY';

  // While the balance loads (e.g. right after a role switch), pass undefined so
  // AccountStatement shimmers instead of rendering an empty zero-balance table.
  const loadedSavingsFunds = savingsFundBalance ? [savingsFundBalance] : [];
  const savingsFunds = savingsFundBalanceLoading ? undefined : loadedSavingsFunds;

  return (
    <section aria-label="represented-party-account">
      {user?.role && (
        <p className={`my-5 m-0 lead ${PII_CLASS}`}>
          <FormattedMessage id="account.legalEntity.greeting" values={{ name: user.role.name }} />
        </p>
      )}

      {showThirdPillar && (
        <>
          <SectionHeading titleId="accountStatement.thirdPillar.heading" />
          <AccountStatement funds={thirdPillarFunds} showProfit />
        </>
      )}

      <SectionHeading titleId="accountStatement.savingsFund.heading">
        <div className="d-flex flex-wrap column-gap-3 row-gap-2 align-items-baseline justify-content-between">
          <Link className="icon-link" to="/savings-fund/payment">
            <FormattedMessage id="accountStatement.savingsFund.deposit" />
          </Link>
          <Link className="icon-link" to="/savings-fund/withdraw">
            <FormattedMessage id="accountStatement.savingsFund.withdraw" />
          </Link>
          {isRepresentedPerson && (
            <Link className="icon-link" to="/savings-fund/gift-link">
              <FormattedMessage id="accountStatement.savingsFund.giftLink" />
            </Link>
          )}
        </div>
      </SectionHeading>
      <AccountStatement funds={savingsFunds} showProfit />

      {isRepresentedCompany && savingsFundBalance && <SavingsFundStatementSection />}

      <ApplicationSection />

      <TransactionSection limit={3} allTransactionsPath="/savings-fund-transactions" />
    </section>
  );
}
